import React, { useState, useContext, ReactNode, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useWindowSize } from 'react-use';

import { demoPagesMenu } from '../../menu';
import { DropdownItem, DropdownMenu } from '../../components/bootstrap/Dropdown';
import Button from '../../components/bootstrap/Button';
import useDarkMode from '../../hooks/useDarkMode';
import Collapse from '../../components/bootstrap/Collapse';
import { NavigationLine } from '../Navigation/Navigation';
import Icon from '../../components/icon/Icon';
import useNavigationItemHandle from '../../hooks/useNavigationItemHandle';
import AuthContext from '../../contexts/authContext';
import ThemeContext from '../../contexts/themeContext';
import authTokenSet from '../../API/authTokenSet';
import LANG, { getLangWithKey, ILang } from '../../lang';  
import showNotification from '../../components/extras/showNotification';




const User = () => {
	const { width } = useWindowSize();
	const { setAsideStatus } = useContext(ThemeContext);
	const { userData, setUser, setToken } = useContext(AuthContext);

	const navigate = useNavigate();
	const handleItem = useNavigationItemHandle();
	const { darkModeStatus, setDarkModeStatus } = useDarkMode();
	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const { i18n } = useTranslation();

	const changeLanguage = (lng: ILang['key']['lng']) => {
		i18n.changeLanguage(lng).then();
		showNotification(
			<span className='d-flex align-items-center'>
				<Icon icon={getLangWithKey(lng)?.icon} size='lg' className='me-1' />
				<span>{`Language changed to ${getLangWithKey(lng)?.text}`}</span>
			</span>,   
			'You updated the language of the site. (Only "Aside" was prepared as an example.)',
		);
	};

	const [collapseStatus, setCollapseStatus] = useState<boolean>(false);
	const { t } = useTranslation(['translation', 'menu']);
	useLayoutEffect(() => {
			document.documentElement.setAttribute('lang', i18n.language.substring(0, 2));
		});

	
	const safeUser = React.useMemo(() => {
	if (!userData) return null;
	if (typeof userData === 'string') {
		try {
		return JSON.parse(userData);
		} catch {
		return null;
		}
	}
	return userData;
	}, [userData]);

	
	

	return (
		<>
			<div
				className={classNames('user', { open: collapseStatus })}
				role='presentation'
				onClick={() => setCollapseStatus(!collapseStatus)}>

				
				<div className='user-avatar d-flex align-items-center justify-content-center'>
					<Icon icon='AccountCircle' size='3x' color='info' />
				</div>

				<div className='user-info'>
					<div className='user-name d-flex align-items-center'>
						{safeUser?.username ||
							safeUser?.name ||
							'Guest User'}
						<Icon icon='Verified' className='ms-1' color='info' />
					</div>
					<div className='user-sub-title'>
						{safeUser?.position || ''}
					</div>
				</div>
			</div>

			
			<DropdownMenu>
				<DropdownItem>
					{/* <Button
						icon='AccountBox'
						onClick={() =>
							navigate(
								`../${demoPagesMenu.appointment.subMenu.employeeID.path}/${safeUser?.id}`,
							)
						}>
						Profile
					</Button> */}
				</DropdownItem>

				<DropdownItem>
					<Button
						icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
						onClick={() => setDarkModeStatus(!darkModeStatus)}>
						{darkModeStatus ? 'Dark Mode' : 'Light Mode'}
					</Button>
				</DropdownItem>

				<DropdownItem>
					<Button
							// eslint-disable-next-line react/jsx-props-no-spreading
							
							icon={fullScreenStatus ? 'FullscreenExit' : 'Fullscreen'}
							onClick={() => setFullScreenStatus(!fullScreenStatus)}
							aria-label='Toggle fullscreen'
						/>
				</DropdownItem>


			</DropdownMenu>

			
			<Collapse isOpen={collapseStatus} className='user-menu'>
				<nav aria-label='aside-bottom-user-menu'>
					<div className='navigation'>
						 <div
							role='presentation'
							className='navigation-item cursor-pointer'
							onClick={() => {
							console.log('CLICK USER →', safeUser);
							console.log('ID →', safeUser?._id);

							navigate(`/profile/${safeUser?._id}`);
							handleItem();
							}}

							>
							<span className='navigation-link navigation-link-pill'>
								<span className='navigation-link-info'>
									<Icon icon='AccountBox' className='navigation-icon' />
									<span className='navigation-text'>
										{t('menu:Profile') as ReactNode}
									</span>
								</span>
							</span>
						</div>

						<div
							role='presentation'
							className='navigation-item cursor-pointer'
							onClick={() => {
								setDarkModeStatus(!darkModeStatus);
								handleItem();
							}}>
							<span className='navigation-link navigation-link-pill'>
								<span className='navigation-link-info'>
									<Icon
										icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
										color={darkModeStatus ? 'info' : 'warning'}
										className='navigation-icon'
									/>
									<span className='navigation-text'>
										{darkModeStatus
											? (t('menu:DarkMode') as ReactNode)
											: (t('menu:LightMode') as ReactNode)}
									</span>
								</span>
							</span>
						</div>
						<div
							role='presentation'
							className='navigation-item cursor-pointer'
							onClick={() => {
								setFullScreenStatus(!fullScreenStatus);
								handleItem();
							}}>
							<span className='navigation-link navigation-link-pill'>
								<span className='navigation-link-info'>
									<Icon
										icon={fullScreenStatus ? 'FullscreenExit' : 'Fullscreen'}
										className='navigation-icon'
									/>
									<span className='navigation-text'>
										{fullScreenStatus ? 'Exit Fullscreen' : 'Fullscreen'}
									</span>
								</span>
							</span>
						</div>

						{/* LANGUAGE */}

	{/* <div
		role='presentation'
		className='navigation-item cursor-pointer'
	>
		<span className='navigation-link navigation-link-pill'>
			<span className='navigation-link-info'>
				<Icon
					icon={getLangWithKey(i18n.language as ILang['key']['lng'])?.icon || 'Language'}
					className='navigation-icon'
				/>
				<span className='navigation-text'>
					{getLangWithKey(i18n.language as ILang['key']['lng'])?.text || 'Language'}
				</span>
			</span>
		</span>
	</div> */}

	{/* LANGUAGE OPTIONS */}
	{/* <div className='ms-4 mt-1'>
		{Object.keys(LANG).map((i) => (
			<div
				key={LANG[i].lng}
				role='presentation'
				className='navigation-item cursor-pointer'
				onClick={() => changeLanguage(LANG[i].lng)}
			>
				<span className='navigation-link navigation-link-pill'>
					<span className='navigation-link-info'>
						<Icon icon={LANG[i].icon} className='navigation-icon' />
						<span className='navigation-text'>{LANG[i].text}</span>
					</span>
				</span>
			</div>
		))}
	</div> */}
</div>


					
				</nav>

				<NavigationLine />

				<nav aria-label='aside-bottom-user-menu-2'>
					<div className='navigation'>
						<div
							role='presentation'
							className='navigation-item cursor-pointer'
							onClick={() => {
								setUser('');
								setToken('');
								authTokenSet(false);
								localStorage.clear();

								if (width < Number(process.env.REACT_APP_MOBILE_BREAKPOINT_SIZE)) {
									setAsideStatus(false);
								}

								navigate(`../${demoPagesMenu.login.path}`);
							}}>
							<span className='navigation-link navigation-link-pill'>
								<span className='navigation-link-info'>
									<Icon icon='Logout' className='navigation-icon' />
									<span className='navigation-text'>
										{t('menu:Logout') as ReactNode}
									</span>
								</span>
							</span>
						</div>
					</div>
				</nav>
			</Collapse>
		</>
	);
};

export default User;
